import { useState, useEffect } from 'react';
import axios from 'axios';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import useIndustries from '../hooks/useIndustries';

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship'];

// Malaysian states/territories - powers the State dropdown (Google addressRegion).
const MY_STATES = [
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perak',
  'Perlis', 'Pulau Pinang', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu',
  'Kuala Lumpur', 'Labuan', 'Putrajaya'
];

// Work arrangement - Remote maps to Google's jobLocationType TELECOMMUTE in job.php.
const WORK_ARRANGEMENTS = ['On-site', 'Remote', 'Hybrid'];

// Education level - readable labels; job.php maps each to Google's credentialCategory.
const EDUCATION_LEVELS = [
  'Not specified', 'SPM / O-Level', 'STPM / Diploma', 'Professional Certificate',
  "Bachelor's Degree", "Master's / Doctorate"
];

const emptyForm = {
  client_id: '', title: '', description: '', description_title: 'Others',
  responsibilities: '', requirements: '',
  salary_min: '', salary_max: '', hide_salary: false, hide_company_overview: false,
  additional_remuneration: '', location: '',
  city: '', state: '', job_location_type: 'On-site', education_level: '', valid_through: '',
  employment_type: 'Full-time', industry: '',
  experience_years_min: '', experience_years_max: '',
  status: 'draft'
};

// Prepend "• " to each non-empty line that isn't already bulleted
function autoBullet(text) {
  if (!text) return '';
  return text.split('\n').map(line => {
    if (!line.trim()) return '';
    const indent = line.match(/^(\s*)/)[1];
    const content = line.trimStart();
    if (/^[•\-\*]/.test(content) || /^\d+\./.test(content)) return line;
    return indent + '• ' + content;
  }).join('\n');
}

const emptyClientOverview = '';

function formatActivityDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }) {
  const styles = {
    draft:    'bg-gray-50 text-gray-500 border-gray-200',
    live:     'bg-green-50 text-green-700 border-green-200',
    archived: 'bg-red-50 text-red-500 border-red-200'
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 border rounded-full ${styles[status] || styles.draft}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// Score a talent pool candidate against a job (0–100)
function matchScore(candidate, job) {
  let score = 0;
  const tags = [];

  // Industry — 40 pts
  if (candidate.industry && job.industry &&
      candidate.industry.toLowerCase().trim() === job.industry.toLowerCase().trim()) {
    score += 40;
    tags.push('Industry');
  }

  // Location — 35 pts (fuzzy: either string contains the other)
  if (candidate.location && job.location) {
    const cl = candidate.location.toLowerCase().trim();
    const jl = job.location.toLowerCase().trim();
    if (cl === jl || cl.includes(jl) || jl.includes(cl)) {
      score += 35;
      tags.push('Location');
    }
  }

  // Experience — 25 pts
  if (candidate.experience_years != null) {
    const exp = parseInt(candidate.experience_years);
    const min = job.experience_years_min != null ? parseInt(job.experience_years_min) : null;
    const max = job.experience_years_max != null ? parseInt(job.experience_years_max) : null;
    if (min != null && max != null && exp >= min && exp <= max) {
      score += 25; tags.push('Experience');
    } else if (min != null && max == null && exp >= min) {
      score += 25; tags.push('Experience');
    } else if (min != null && (exp === min - 1 || exp === max + 1)) {
      score += 12; tags.push('Exp. close');
    }
  }

  return { score, tags };
}

export default function Jobs() {
  const [jobs, setJobs]               = useState([]);
  const [clients, setClients]         = useState([]);
  const [talentPool, setTalentPool]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [filterStatus, setFilter]     = useState('live');
  const [selected, setSelected]       = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [editMode, setEditMode]       = useState(false);
  const [form, setForm]               = useState(emptyForm);
  const [submitting, setSubmitting]   = useState(false);
  const [formError, setFormError]     = useState('');
  const [clientOverview, setClientOverview] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [jobActivity, setJobActivity] = useState([]);
  const { markClean, confirmClose } = useUnsavedChanges();
  const { industries, addIndustry } = useIndustries();

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchJobs();
    fetchClients();
    fetchTalentPool();
  }, []);

  useEffect(() => {
    if (!selected?.id) { setJobActivity([]); return; }
    axios.get(`https://carriera-backend.onrender.com/api/jobs/${selected.id}/activity`, { headers })
      .then(res => setJobActivity(res.data))
      .catch(() => setJobActivity([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  async function fetchJobs() {
    try {
      const res = await axios.get('https://carriera-backend.onrender.com/api/jobs', { headers });
      setJobs(res.data);
    } catch {
      setError('Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchClients() {
    try {
      const res = await axios.get('https://carriera-backend.onrender.com/api/clients', { headers });
      setClients(res.data);
    } catch {}
  }

  async function fetchTalentPool() {
    try {
      const res = await axios.get('https://carriera-backend.onrender.com/api/applications', { headers });
      setTalentPool(res.data.filter(c => c.status === 'placed' || c.status === 'rejected'));
    } catch {}
  }

  function handleClientChange(clientId) {
    setForm(p => ({ ...p, client_id: clientId }));
    const client = clients.find(c => c.id === clientId);
    if (client?.company_overview) {
      setClientOverview(client.company_overview);
    } else {
      setClientOverview('');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    if (!form.title) { setFormError('Job title is required.'); return; }

    // A reason is mandatory when the salary changes.
    if (editMode && selected) {
      const salaryChanged =
        String(form.salary_min ?? '') !== String(selected.salary_min ?? '') ||
        String(form.salary_max ?? '') !== String(selected.salary_max ?? '');
      if (salaryChanged && !changeReason.trim()) {
        setFormError('Please add a reason for the salary change.');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (editMode && selected) {
        const res = await axios.patch(`https://carriera-backend.onrender.com/api/jobs/${selected.id}`, { ...form, change_reason: changeReason }, { headers });
        setJobs(prev => prev.map(j => j.id === selected.id ? res.data : j));
        setSelected(res.data);
        if (selected.id) {
          axios.get(`https://carriera-backend.onrender.com/api/jobs/${selected.id}/activity`, { headers })
            .then(r => setJobActivity(r.data)).catch(() => {});
        }
      } else {
        const res = await axios.post('https://carriera-backend.onrender.com/api/jobs', form, { headers });
        setJobs(prev => [res.data, ...prev]);
      }
      setShowForm(false);
      setEditMode(false);
      setForm(emptyForm);
      setChangeReason('');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function updateStatus(jobId, status) {
    try {
      const res = await axios.patch(`https://carriera-backend.onrender.com/api/jobs/${jobId}/status`, { status }, { headers });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: res.data.status } : j));
      if (selected?.id === jobId) setSelected(prev => ({ ...prev, status: res.data.status }));
    } catch {
      alert('Failed to update status.');
    }
  }

  function openEdit(job) {
    const client = clients.find(c => c.id === job.client_id);
    const overview = client?.company_overview || '';
    const next = {
      client_id:            job.client_id || '',
      title:                job.title || '',
      description:          job.description || '',
      description_title:    job.description_title || 'Others',
      responsibilities:     job.responsibilities || '',
      requirements:         job.requirements || '',
      salary_min:           job.salary_min || '',
      salary_max:           job.salary_max || '',
      hide_salary:          job.hide_salary || false,
      hide_company_overview: job.hide_company_overview || false,
      additional_remuneration: job.additional_remuneration || '',
      location:             job.location || '',
      city:                 job.city || '',
      state:                job.state || '',
      job_location_type:    job.job_location_type || 'On-site',
      education_level:      job.education_level || '',
      valid_through:        job.valid_through || '',
      employment_type:      job.employment_type || 'Full-time',
      industry:             job.industry || '',
      experience_years_min: job.experience_years_min || '',
      experience_years_max: job.experience_years_max || '',
      status:               job.status || 'draft'
    };
    setClientOverview(overview);
    setForm(next);
    setChangeReason('');
    setEditMode(true);
    setShowForm(true);
    markClean({ ...next, clientOverview: overview });
  }

  const [generatingPDF, setGeneratingPDF] = useState(false);

  async function downloadPDF(job) {
    setGeneratingPDF(true);
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const { default: JDPdf } = await import('../JDPdf');
      const clientOverview = clients.find(c => c.id === job.client_id)?.company_overview || '';
      const blob = await pdf(<JDPdf job={job} clientOverview={clientOverview} descriptionTitle={job.description_title || 'Others'} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `JD_${job.title.replace(/\s+/g, '_')}_Carriera.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF error:', err);
      alert('PDF generation failed. Using print instead.');
      printJD(job);
    } finally {
      setGeneratingPDF(false);
    }
  }

  function printJD(job) {
    const clientOverview = clients.find(c => c.id === job.client_id)?.company_overview || '';

    const toLines = (text) => {
      if (!text) return '';
      return text.split('\n').map(l => `<div style="line-height:1.7; font-size:10pt;">${l}</div>`).join('');
    };

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>JD - ${job.title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 10pt; color: #1C1C1A; padding: 40px 50px; }
    .logo { width: 130px; margin-bottom: 14px; }
    .company-details { border-bottom: 0.5px solid #888; padding-bottom: 10px; margin-bottom: 16px; }
    .company-name { font-weight: bold; font-size: 9pt; text-decoration: underline; margin-bottom: 3px; }
    .company-info { font-size: 8.5pt; color: #333; line-height: 1.5; }
    .doc-title { text-align: center; font-size: 12pt; font-weight: bold; text-decoration: underline; margin-bottom: 14px; }
    .meta-row { display: flex; margin-bottom: 4px; }
    .meta-label { width: 90px; font-size: 10pt; color: #333; }
    .meta-value { font-size: 10pt; }
    .divider { border-bottom: 0.75px solid #333; margin: 12px 0 14px; }
    .section-heading { font-weight: bold; text-decoration: underline; margin: 12px 0 6px; font-size: 10pt; }
    .paragraph { font-size: 10pt; line-height: 1.6; margin-bottom: 6px; }
    ul { padding-left: 18px; margin: 0; }
    li { font-size: 10pt; line-height: 1.7; margin-bottom: 2px; }
    .cta { text-align: center; font-weight: bold; margin-top: 30px; font-size: 10pt; line-height: 1.8; }
    .cta a { color: #7A6B40; }
    @media print { body { padding: 30px 40px; } }
  </style>
</head>
<body>
  <img class="logo" src="${window.location.origin}/Carriera_Logo_Official.png" alt="Carriera" />

  <div class="company-details">
    <div class="company-name">Agensi Pekerjaan Carriera Talent Resources Sdn Bhd (1058296-H)</div>
    <div class="company-info">15-2-1, (Level 1, Room 7), Jalan Setia Prima R U13/R, Setia Alam, 40170 Shah Alam, Selangor.</div>
    <div class="company-info">Tel: 017-3188539&nbsp;&nbsp;E-mail: enquiry@carriera.com.my&nbsp;&nbsp;Website: carriera.com.my</div>
  </div>

  <div class="doc-title">Job Description</div>

  <div class="meta-row"><span class="meta-label">Position:</span><span class="meta-value">${job.title}</span></div>
  ${job.location ? `<div class="meta-row"><span class="meta-label">Location:</span><span class="meta-value">${job.location}</span></div>` : ''}

  <div class="divider"></div>

  ${clientOverview ? `<div class="section-heading">Company Overview</div><div class="paragraph">${toLines(clientOverview)}</div>` : ''}
  ${job.responsibilities ? `<div class="section-heading">Responsibilities:</div><div class="paragraph">${toLines(job.responsibilities)}</div>` : ''}
  ${job.requirements ? `<div class="section-heading">Requirements:</div><div class="paragraph">${toLines(job.requirements)}</div>` : ''}
  ${job.description ? `<div class="section-heading">Others:</div><div class="paragraph">${toLines(job.description)}</div>` : ''}

  <div class="cta">
    Interested candidates please send your resume to enquiry@carriera.com.my<br/>
    or call us at 017-3188539.
  </div>

  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  }

  const filtered = jobs.filter(j => {
    const matchSearch = !search ||
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      (j.location || '').toLowerCase().includes(search.toLowerCase()) ||
      (j.clients?.company_name || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || j.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const inputClass  = "w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200";
  const labelClass  = "block text-xs text-carriera-muted uppercase tracking-widest mb-1";

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-carriera-muted text-sm">Loading jobs...</p>
    </div>
  );

  return (
    <div className="h-full flex flex-col">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Active Positions</p>
          <h2 className="font-serif text-3xl font-normal text-carriera-text">Jobs</h2>
        </div>
        <button onClick={() => { setShowForm(true); setEditMode(false); setForm(emptyForm); setClientOverview(emptyClientOverview); setFormError(''); markClean({ ...emptyForm, clientOverview: emptyClientOverview }); }}
          className="bg-carriera-text text-white text-sm font-medium px-4 py-2 hover:bg-carriera-accent transition-colors duration-200">
          + New Job
        </button>
      </div>

      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Jobs', value: jobs.length },
          { label: 'Live',       value: jobs.filter(j => j.status === 'live').length },
          { label: 'Draft',      value: jobs.filter(j => j.status === 'draft').length },
        ].map(s => (
          <div key={s.label} className="bg-white border border-carriera-border p-4"
               style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">{s.label}</p>
            <p className="font-serif text-2xl font-normal text-carriera-text">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 mb-6">
        <input type="text" placeholder="Search by title, client or location..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200" />
        <select value={filterStatus} onChange={e => setFilter(e.target.value)}
          className="border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent transition-colors duration-200">
          <option value="all">All Status</option>
          <option value="live">Live</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Jobs list */}
      <div className="bg-white border border-carriera-border flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-carriera-muted text-sm">
              {jobs.length === 0 ? 'No jobs yet. Create your first job posting.' : 'No jobs match your search.'}
            </p>
          </div>
        ) : (
          filtered.map((job, i) => (
            <div key={job.id}
              onClick={() => setSelected(job)}
              className={`flex items-center justify-between p-4 cursor-pointer hover:bg-carriera-bg transition-colors duration-200 ${i > 0 ? 'border-t border-carriera-border' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-carriera-text">{job.title}</p>
                <p className="text-xs text-carriera-muted mt-0.5">
                  {job.clients?.company_name || 'No client'} &nbsp;·&nbsp;
                  {job.location || '—'} &nbsp;·&nbsp;
                  {job.salary_min && job.salary_max ? `MYR ${Number(job.salary_min).toLocaleString()}–${Number(job.salary_max).toLocaleString()}` : 'Salary TBD'}
                </p>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <StatusBadge status={job.status} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── JOB DETAIL PANEL ── */}
      {selected && !showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            <div className="p-6 border-b border-carriera-border flex items-start justify-between">
              <div>
                <h3 className="font-serif text-xl font-normal text-carriera-text">{selected.title}</h3>
                <p className="text-sm text-carriera-muted mt-0.5">{selected.clients?.company_name || 'No client assigned'}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={async () => {
                  if (!window.confirm(`Delete "${selected.title}"? This cannot be undone.`)) return;
                  try {
                    await axios.delete(`https://carriera-backend.onrender.com/api/jobs/${selected.id}`, { headers });
                    setJobs(prev => prev.filter(j => j.id !== selected.id));
                    setSelected(null);
                  } catch { alert('Failed to delete job.'); }
                }} className="text-xs text-red-400 hover:text-red-600 transition-colors duration-200">
                  Delete
                </button>
                <button onClick={() => setSelected(null)} className="text-carriera-muted hover:text-carriera-text text-lg">✕</button>
              </div>
            </div>

            <div className="p-6 space-y-5">

              {/* Status toggle */}
              <div>
                <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Status</p>
                <div className="flex gap-2">
                  {['draft', 'live', 'archived'].map(s => (
                    <button key={s} onClick={() => updateStatus(selected.id, s)}
                      className={`flex-1 text-xs py-2 border transition-colors duration-200 ${
                        selected.status === s
                          ? 'bg-carriera-accent text-white border-carriera-accent'
                          : 'bg-white text-carriera-sec border-carriera-border hover:border-carriera-accent'
                      }`}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
                {selected.status === 'live' && (
                  <p className="text-xs text-green-600 mt-2">✓ This job is live and visible on your website.</p>
                )}
              </div>

              <hr className="border-carriera-border" />

              {/* Job details */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Area',            value: selected.location || '—' },
                  { label: 'City',            value: selected.city || '—' },
                  { label: 'State',           value: selected.state || '—' },
                  { label: 'Work Arrangement', value: selected.job_location_type || '—' },
                  { label: 'Employment Type', value: selected.employment_type || '—' },
                  { label: 'Education Level', value: selected.education_level || '—' },
                  { label: 'Industry',        value: selected.industry || '—' },
                  { label: 'Salary Range',    value: `${selected.salary_min && selected.salary_max ? `MYR ${Number(selected.salary_min).toLocaleString()} – ${Number(selected.salary_max).toLocaleString()}/month` : '—'}${selected.hide_salary ? '  ·  Hidden on website' : ''}` },
                  ...(selected.hide_company_overview ? [{ label: 'Company Overview', value: 'Hidden on website' }] : []),
                  { label: 'Additional Remuneration', value: selected.additional_remuneration || '—' },
                  { label: 'Min. Experience', value: selected.experience_years_min ? `${selected.experience_years_min} years` : '—' },
                  { label: 'Max. Experience', value: selected.experience_years_max ? `${selected.experience_years_max} years` : '—' },
                  { label: 'Posting Expiry',  value: selected.valid_through || 'Auto (60 days)' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-xs text-carriera-muted uppercase tracking-widest mb-0.5">{item.label}</p>
                    <p className="text-sm text-carriera-text">{item.value}</p>
                  </div>
                ))}
              </div>

              {selected.responsibilities && (
                <div>
                  <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Responsibilities</p>
                  <p className="text-sm text-carriera-text leading-relaxed whitespace-pre-line">{selected.responsibilities}</p>
                </div>
              )}

              {selected.requirements && (
                <div>
                  <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Requirements</p>
                  <p className="text-sm text-carriera-text leading-relaxed whitespace-pre-line">{selected.requirements}</p>
                </div>
              )}

              {selected.description && (
                <div>
                  <p className="text-xs text-carriera-muted uppercase tracking-widest mb-1">Others</p>
                  <p className="text-sm text-carriera-text leading-relaxed whitespace-pre-line">{selected.description}</p>
                </div>
              )}

              <hr className="border-carriera-border" />

              {/* Suggested from Talent Pool */}
              {(() => {
                const matches = talentPool
                  .map(c => ({ ...c, ...matchScore(c, selected) }))
                  .filter(c => c.score >= 35)
                  .sort((a, b) => b.score - a.score)
                  .slice(0, 5);
                return (
                  <div>
                    <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Suggested from Talent Pool</p>
                    {matches.length === 0 ? (
                      <p className="text-xs text-carriera-muted italic">No matching candidates in talent pool yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {matches.map(c => (
                          <div key={c.id} className="border border-carriera-border bg-carriera-bg p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-carriera-text">{c.candidate_name}</p>
                                <p className="text-xs text-carriera-muted mt-0.5">
                                  {c.location || '—'} · {c.experience_years ? `${c.experience_years} yrs` : 'Exp N/A'} · {c.industry || '—'}
                                </p>
                              </div>
                              <div className="flex-shrink-0 text-right">
                                <span className={`text-xs font-semibold ${c.score >= 75 ? 'text-green-600' : c.score >= 50 ? 'text-amber-600' : 'text-carriera-muted'}`}>
                                  {c.score}%
                                </span>
                                <div className="flex gap-1 mt-1 justify-end flex-wrap">
                                  {c.tags.map(t => (
                                    <span key={t} className="text-xs bg-white border border-carriera-border px-1.5 py-0.5 text-carriera-muted">{t}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              <hr className="border-carriera-border" />

              {/* JD Preview */}
              <div>
                <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">JD Preview</p>
                <div className="border border-carriera-border overflow-y-auto" style={{ maxHeight: 480, backgroundColor: '#fff' }}>
                  <div style={{ padding: '24px 28px', fontFamily: 'Arial, sans-serif', fontSize: '9pt', color: '#1C1C1A', lineHeight: 1.5 }}>

                    {/* Logo */}
                    <img src="/Carriera_Logo_Official.png" alt="Carriera" style={{ width: 100, marginBottom: 10 }} />

                    {/* Letterhead */}
                    <div style={{ borderBottom: '0.5px solid #888', paddingBottom: 8, marginBottom: 12 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '7.5pt', textDecoration: 'underline', marginBottom: 2 }}>
                        Agensi Pekerjaan Carriera Talent Resources Sdn Bhd (1058296-H)
                      </div>
                      <div style={{ fontSize: '7pt', color: '#333' }}>
                        15-2-1, (Level 1, Room 7), Jalan Setia Prima R U13/R, Setia Alam, 40170 Shah Alam, Selangor.
                      </div>
                      <div style={{ fontSize: '7pt', color: '#333' }}>
                        Tel: 017-3188539 &nbsp;&nbsp; E-mail: enquiry@carriera.com.my &nbsp;&nbsp; Website: carriera.com.my
                      </div>
                    </div>

                    {/* Title */}
                    <div style={{ textAlign: 'center', fontWeight: 'bold', textDecoration: 'underline', fontSize: '10pt', marginBottom: 12 }}>
                      Job Description
                    </div>

                    {/* Position & Location */}
                    <div style={{ display: 'flex', marginBottom: 3 }}>
                      <span style={{ width: 68, flexShrink: 0, color: '#333' }}>Position:</span>
                      <span>{selected.title}</span>
                    </div>
                    {selected.location && (
                      <div style={{ display: 'flex', marginBottom: 3 }}>
                        <span style={{ width: 68, flexShrink: 0, color: '#333' }}>Location:</span>
                        <span>{selected.location}</span>
                      </div>
                    )}

                    {/* Divider */}
                    <div style={{ borderBottom: '0.75px solid #333', margin: '10px 0 12px' }} />

                    {/* Company Overview */}
                    {clients.find(c => c.id === selected.client_id)?.company_overview && (
                      <>
                        <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: 4, marginTop: 8 }}>Company Overview</div>
                        <div style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>{clients.find(c => c.id === selected.client_id).company_overview}</div>
                      </>
                    )}

                    {/* Responsibilities */}
                    {selected.responsibilities && (
                      <>
                        <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: 4, marginTop: 8 }}>Responsibilities:</div>
                        <div style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>{autoBullet(selected.responsibilities)}</div>
                      </>
                    )}

                    {/* Requirements */}
                    {selected.requirements && (
                      <>
                        <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: 4, marginTop: 8 }}>Requirements:</div>
                        <div style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>{autoBullet(selected.requirements)}</div>
                      </>
                    )}

                    {/* Others */}
                    {selected.description && (
                      <>
                        <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: 4, marginTop: 8 }}>{selected.description_title || 'Others'}:</div>
                        <div style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>{selected.description}</div>
                      </>
                    )}

                    {/* CTA */}
                    <div style={{ textAlign: 'center', fontWeight: 'bold', marginTop: 24, lineHeight: 1.8 }}>
                      Interested candidates please send your resume to{' '}
                      <span style={{ color: '#7A6B40' }}>enquiry@carriera.com.my</span>
                      {' '}or call us at 017-3188539.
                    </div>

                  </div>
                </div>
              </div>

              {/* Activity Log */}
              <div>
                <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Activity Log</p>
                {jobActivity.length === 0 ? (
                  <p className="text-xs text-carriera-muted italic">No activity recorded yet.</p>
                ) : (
                  <div className="space-y-0">
                    {jobActivity.map((entry, idx) => (
                      <div key={entry.id} className="flex gap-3">
                        <div className="flex flex-col items-center pt-1">
                          <div className="w-2 h-2 rounded-full bg-carriera-accent flex-shrink-0" />
                          {idx < jobActivity.length - 1 && (
                            <div className="flex-1 w-px bg-carriera-border mt-1" style={{ minHeight: 20 }} />
                          )}
                        </div>
                        <div className="pb-4 flex-1">
                          <p className="text-xs text-carriera-text leading-relaxed">{entry.details}</p>
                          <p className="text-xs text-carriera-muted mt-0.5">{formatActivityDate(entry.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Download PDF button */}
              <button
                onClick={() => downloadPDF(selected)}
                disabled={generatingPDF}
                className="w-full bg-carriera-accent text-white text-sm font-medium py-2.5 hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                {generatingPDF ? 'Generating PDF...' : '↓ Download JD PDF'}
              </button>

              <button onClick={() => openEdit(selected)}
                className="w-full bg-carriera-text text-white text-sm font-medium py-2.5 hover:bg-carriera-accent transition-colors duration-200">
                Edit Job
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT JOB FORM ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => confirmClose({ ...form, clientOverview }, () => { setShowForm(false); setEditMode(false); })}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            <div className="p-6 border-b border-carriera-border flex items-start justify-between">
              <div>
                <h3 className="font-serif text-xl font-normal text-carriera-text">
                  {editMode ? 'Edit Job' : 'New Job Posting'}
                </h3>
                <p className="text-sm text-carriera-muted mt-0.5">
                  {editMode ? 'Update job details' : 'Create a new position'}
                </p>
              </div>
              <button onClick={() => confirmClose({ ...form, clientOverview }, () => { setShowForm(false); setEditMode(false); })}
                className="text-carriera-muted hover:text-carriera-text text-lg">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Position Details</p>
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Job Title <span className="text-red-500">*</span></label>
                    <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. Senior Accountant" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Client</label>
                    <select value={form.client_id} onChange={e => handleClientChange(e.target.value)} className={inputClass}>
                      <option value="">Select client (optional)…</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Area / Neighbourhood</label>
                      <input type="text" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                        placeholder="e.g. Bandar Bukit Raja" className={inputClass} />
                      <p className="text-xs text-carriera-muted mt-1">The specific area. Add City + State so Google shows it for city searches.</p>
                    </div>
                    <div>
                      <label className={labelClass}>City</label>
                      <input type="text" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                        placeholder="e.g. Klang" className={inputClass} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>State</label>
                      <select value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} className={inputClass}>
                        <option value="">Select state…</option>
                        {MY_STATES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Work Arrangement</label>
                      <select value={form.job_location_type} onChange={e => setForm(p => ({ ...p, job_location_type: e.target.value }))} className={inputClass}>
                        {WORK_ARRANGEMENTS.map(w => <option key={w}>{w}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Employment Type</label>
                      <select value={form.employment_type} onChange={e => setForm(p => ({ ...p, employment_type: e.target.value }))} className={inputClass}>
                        {EMPLOYMENT_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Education Level</label>
                      <select value={form.education_level} onChange={e => setForm(p => ({ ...p, education_level: e.target.value }))} className={inputClass}>
                        <option value="">Not specified</option>
                        {EDUCATION_LEVELS.filter(edu => edu !== 'Not specified').map(edu => <option key={edu}>{edu}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Industry</label>
                    <input type="text" list="industries-list" value={form.industry}
                      onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                      onBlur={e => addIndustry(e.target.value)}
                      placeholder="Select or type a new industry…" className={inputClass} />
                    <datalist id="industries-list">
                      {industries.map(i => <option key={i} value={i} />)}
                    </datalist>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Salary Min (MYR)</label>
                      <input type="number" value={form.salary_min} onChange={e => setForm(p => ({ ...p, salary_min: e.target.value }))}
                        placeholder="e.g. 5000" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Salary Max (MYR)</label>
                      <input type="number" value={form.salary_max} onChange={e => setForm(p => ({ ...p, salary_max: e.target.value }))}
                        placeholder="e.g. 7000" className={inputClass} />
                    </div>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={!!form.hide_salary}
                      onChange={e => setForm(p => ({ ...p, hide_salary: e.target.checked }))}
                      className="mt-0.5 accent-carriera-accent" />
                    <span className="text-sm text-carriera-text">
                      Hide salary range on website
                      <span className="block text-xs text-carriera-muted mt-0.5">
                        The salary stays in your dashboard but isn't shown to website visitors.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={!!form.hide_company_overview}
                      onChange={e => setForm(p => ({ ...p, hide_company_overview: e.target.checked }))}
                      className="mt-0.5 accent-carriera-accent" />
                    <span className="text-sm text-carriera-text">
                      Hide company overview on website
                      <span className="block text-xs text-carriera-muted mt-0.5">
                        For clients who want a specific vacancy kept confidential. Applies to this job only - the client's other live jobs still show their overview.
                      </span>
                    </span>
                  </label>
                  <div>
                    <label className={labelClass}>Additional Remuneration / Notes <span className="text-carriera-muted font-normal">(optional)</span></label>
                    <input type="text" value={form.additional_remuneration} onChange={e => setForm(p => ({ ...p, additional_remuneration: e.target.value }))}
                      placeholder="e.g. + sales commission, RM500 transport allowance" className={inputClass} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Min. Exp. (years)</label>
                      <input type="number" value={form.experience_years_min} onChange={e => setForm(p => ({ ...p, experience_years_min: e.target.value }))}
                        placeholder="e.g. 3" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Max. Exp. (years)</label>
                      <input type="number" value={form.experience_years_max} onChange={e => setForm(p => ({ ...p, experience_years_max: e.target.value }))}
                        placeholder="e.g. 7" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Posting Expiry Date <span className="text-carriera-muted font-normal">(optional)</span></label>
                    <input type="date" value={form.valid_through || ''} onChange={e => setForm(p => ({ ...p, valid_through: e.target.value }))}
                      className={inputClass} />
                    <p className="text-xs text-carriera-muted mt-1">When the job should drop off Google. Internal only — not shown on the website. Leave blank for auto 60 days.</p>
                  </div>
                </div>
              </div>

              <hr className="border-carriera-border" />

              <div>
                <p className="text-xs font-medium text-carriera-accent uppercase tracking-widest mb-3">Job Description</p>
                <div className="space-y-4">

                  <div>
                    <label className={labelClass}>1. Company Overview</label>
                    <p className="text-xs text-carriera-muted mb-1">
                      {form.client_id && clientOverview
                        ? 'Auto-filled from client profile. Edit if needed.'
                        : 'Select a client above to auto-fill, or type manually.'}
                    </p>
                    <textarea value={clientOverview} onChange={e => setClientOverview(e.target.value)}
                      rows={4} placeholder="Describe the company without mentioning the name..."
                      className={inputClass} style={{ resize: 'vertical' }} />
                  </div>

                  <div>
                    <label className={labelClass}>2. Responsibilities</label>
                    <textarea value={form.responsibilities} onChange={e => setForm(p => ({ ...p, responsibilities: e.target.value }))}
                      rows={6} placeholder={"Assist Finance Manager in daily operations\nCheck daily bank transactions and payments\nPrepare payment vouchers"}
                      className={inputClass} style={{ resize: 'vertical' }} />
                    <p className="text-xs text-carriera-muted mt-1">One item per line — bullet points added automatically in PDF.</p>
                  </div>

                  <div>
                    <label className={labelClass}>3. Requirements</label>
                    <textarea value={form.requirements} onChange={e => setForm(p => ({ ...p, requirements: e.target.value }))}
                      rows={6} placeholder={"Minimum Diploma in Accounting or related field\nBasic accounting knowledge is required\nProficient in Microsoft Excel and Word"}
                      className={inputClass} style={{ resize: 'vertical' }} />
                    <p className="text-xs text-carriera-muted mt-1">One item per line — bullet points added automatically in PDF.</p>
                  </div>

                  <div>
                    <label className={labelClass}>4. Section Title <span className="text-carriera-muted font-normal">(optional — hidden if content is empty)</span></label>
                    <input type="text" value={form.description_title}
                      onChange={e => setForm(p => ({ ...p, description_title: e.target.value }))}
                      placeholder="e.g. Others, Benefits, Working Hours..."
                      className={inputClass + ' mb-2'} />
                    <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                      rows={4} placeholder="Any additional information, working hours, benefits, etc..."
                      className={inputClass} style={{ resize: 'vertical' }} />
                  </div>

                </div>
              </div>

              <hr className="border-carriera-border" />

              <div>
                <label className={labelClass}>Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className={inputClass}>
                  <option value="draft">Draft — not visible on website</option>
                  <option value="live">Live — visible on website</option>
                  <option value="archived">Archived — closed position</option>
                </select>
              </div>

              {editMode && (
                <div>
                  <label className={labelClass}>Reason for change <span className="text-carriera-muted font-normal">(optional)</span></label>
                  <textarea rows={2} value={changeReason} onChange={e => setChangeReason(e.target.value)}
                    placeholder="e.g. Client raised the budget" className={inputClass + ' resize-none'} />
                  <p className="text-xs text-carriera-muted mt-1">Saved to this job's activity log alongside what changed.</p>
                </div>
              )}

              {formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{formError}</div>
              )}

              <button type="submit" disabled={submitting}
                className="w-full bg-carriera-text text-white text-sm font-medium py-2.5 hover:bg-carriera-accent transition-colors duration-200 disabled:opacity-50">
                {submitting ? 'Saving...' : editMode ? 'Save Changes' : 'Create Job'}
              </button>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}
